import React, { useState } from 'react';
import { addEventItem, updateEventItem } from '../../utils/eventApi';
import { formatMoney, toShareRequests } from '../../utils/eventUtils';
import SplitEditor, { getRemainingAmount } from './SplitEditor';

let nextPartKey = 1;

// Adds or edits one line item of an event (e.g. "Room booking" or "Food"). The split is optional
// and can be done in parts: 10,000 among everyone, 4,000 between two people, and so on.
// The total can be raised or lowered later; a fully split item has to be reopened first because
// it is already counted in balances.
function EventItemModal({ event, users, currentUser, itemToEdit, changeTotal = false, onClose, onSuccess }) {
  const isEditing = !!itemToEdit;
  const members = users.filter((u) => event.memberIds.includes(u.id));

  const [name, setName] = useState(isEditing ? itemToEdit.name : '');
  const [totalAmount, setTotalAmount] = useState(isEditing ? itemToEdit.totalAmount.toString() : '');
  const [parts, setParts] = useState(() =>
    (isEditing ? itemToEdit.parts || [] : []).map((part) => {
      const exactAmounts = {};
      part.shares.forEach((s) => {
        exactAmounts[s.userId] = s.amount.toString();
      });
      return {
        key: nextPartKey++,
        name: part.name || '',
        amount: part.amount.toString(),
        splitType: part.splitType === 'EQUAL' ? 'equal' : 'exact',
        selectedParticipants: part.shares.map((s) => s.userId),
        exactAmounts,
      };
    })
  );
  const [expectedPayerId, setExpectedPayerId] = useState(isEditing ? itemToEdit.expectedPayerId || '' : '');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const originalTotal = isEditing ? Number(itemToEdit.totalAmount) : 0;
  const originalAllocated = isEditing
    ? (itemToEdit.parts || []).reduce((sum, part) => sum + Number(part.amount), 0)
    : 0;
  const wasFullySplit = isEditing && originalTotal > 0 && Math.abs(originalTotal - originalAllocated) < 0.005;
  const paidSoFar = isEditing
    ? Number(event.summary?.items?.find((i) => i.itemId === itemToEdit.id)?.paid || 0)
    : 0;
  const [totalUnlocked, setTotalUnlocked] = useState(!wasFullySplit);
  const [confirmingReopen, setConfirmingReopen] = useState(wasFullySplit && changeTotal);
  const [adjustBy, setAdjustBy] = useState('');
  const [changeNote, setChangeNote] = useState('');

  const total = parseFloat(totalAmount) || 0;
  const totalChanged = isEditing && Math.abs(total - originalTotal) >= 0.005;
  const allocated = parts.reduce((sum, part) => sum + (parseFloat(part.amount) || 0), 0);
  const unsplit = parseFloat((total - allocated).toFixed(2));
  const isOverAllocated = unsplit < -0.001;
  const hasUnbalancedPart = parts.some(
    (part) =>
      part.splitType === 'exact' &&
      Math.abs(getRemainingAmount(part.amount, part.selectedParticipants, part.exactAmounts)) >= 0.01
  );
  const isSubmitDisabled = loading || isOverAllocated || hasUnbalancedPart;

  const updatePart = (key, changes) => {
    setParts(parts.map((part) => (part.key === key ? { ...part, ...changes } : part)));
  };

  const addPart = () => {
    setParts([
      ...parts,
      {
        key: nextPartKey++,
        name: '',
        amount: unsplit > 0 ? unsplit.toFixed(2) : '',
        splitType: 'equal',
        selectedParticipants: members.map((u) => u.id),
        exactAmounts: {},
      },
    ]);
  };

  const adjustTotal = (sign) => {
    const delta = parseFloat(adjustBy);
    if (!(delta > 0)) return;
    setTotalAmount(Math.max(0, total + sign * delta).toFixed(2));
    setAdjustBy('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!name.trim() || !totalAmount || total <= 0) {
      setError('Please provide a valid item name and total.');
      return;
    }
    for (const part of parts) {
      if (!(parseFloat(part.amount) > 0)) {
        setError('Every part of the split needs an amount.');
        return;
      }
      if (part.selectedParticipants.length === 0) {
        setError('Every part of the split needs at least one participant.');
        return;
      }
    }
    if (isOverAllocated) {
      setError(`The split is ${formatMoney(Math.abs(unsplit))} more than the total.`);
      return;
    }
    if (total < paidSoFar - 0.001) {
      setError(`${formatMoney(paidSoFar)} has already been paid on this bill, so the total can't go below that.`);
      return;
    }

    const item = {
      name: name.trim(),
      totalAmount: total,
      parts: parts.map((part) => ({
        name: part.name.trim() || null,
        amount: parseFloat(part.amount),
        splitType: part.splitType.toUpperCase(),
        shares: toShareRequests(part.splitType, part.selectedParticipants, part.exactAmounts),
      })),
      expectedPayerId: expectedPayerId || null,
    };
    if (totalChanged) {
      item.totalChangeNote = changeNote.trim() || null;
      item.reopenSplit = wasFullySplit;
    }

    setLoading(true);
    try {
      if (isEditing) {
        await updateEventItem(event.id, itemToEdit.id, item);
      } else {
        await addEventItem(event.id, item);
      }
      onSuccess();
      onClose();
    } catch (err) {
      setError(err.message || 'Error saving item.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop">
      <div className="modal-content animate-slide-up">
        <div className="modal-header">
          <h3>{!isEditing ? '➕ Add Item' : changeTotal ? '✏️ Change Total' : '✏️ Edit Item'}</h3>
          <button className="btn-close" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && <div className="modal-error">{error}</div>}

        <form onSubmit={handleSubmit} className="modal-form">
          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="item-name">Item</label>
              <input
                id="item-name"
                type="text"
                placeholder="e.g. Room booking, Food"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="item-total">Total Amount (₹)</label>
              <input
                id="item-total"
                type="number"
                step="0.01"
                placeholder="0.00"
                value={totalAmount}
                onChange={(e) => setTotalAmount(e.target.value)}
                disabled={!totalUnlocked}
                required
              />
            </div>
          </div>

          {isEditing && !totalUnlocked && !confirmingReopen && (
            <div className="total-lock-row">
              <span className="event-form-section-hint">
                This bill is fully split and counted in balances, so its total is locked.
              </span>
              <button type="button" className="btn-tiny" onClick={() => setConfirmingReopen(true)}>
                Change total
              </button>
            </div>
          )}

          {isEditing && !totalUnlocked && confirmingReopen && (
            <div className="remaining-amount-banner pending total-reopen-banner">
              <span>
                Changing the total reopens the split. Payments on this bill leave balances until the whole new
                total is split again. If you split the difference before saving, balances simply update.
              </span>
              <div className="total-reopen-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    setTotalUnlocked(true);
                    setConfirmingReopen(false);
                  }}
                >
                  Reopen and change total
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={() => setConfirmingReopen(false)}>
                  Keep total
                </button>
              </div>
            </div>
          )}

          {isEditing && totalUnlocked && (
            <div className="total-adjust-row">
              <div className="form-group">
                <label htmlFor="item-adjust-by">Add or reduce by (₹)</label>
                <input
                  id="item-adjust-by"
                  type="number"
                  step="0.01"
                  placeholder="e.g. 2000"
                  value={adjustBy}
                  onChange={(e) => setAdjustBy(e.target.value)}
                />
              </div>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => adjustTotal(1)}>
                ＋ Add
              </button>
              <button type="button" className="btn btn-secondary btn-sm" onClick={() => adjustTotal(-1)}>
                － Reduce
              </button>
            </div>
          )}

          {totalChanged && (
            <div className="form-group">
              <label htmlFor="item-change-note">Reason for the change (optional)</label>
              <input
                id="item-change-note"
                type="text"
                maxLength={200}
                placeholder="e.g. Extra bed, refund"
                value={changeNote}
                onChange={(e) => setChangeNote(e.target.value)}
              />
              <span className="event-form-section-hint">
                Total changes from {formatMoney(originalTotal)} to {formatMoney(total)}.
              </span>
            </div>
          )}

          <div className="event-form-section">
            <span className="event-form-section-title">Split</span>
            <span className="event-form-section-hint">
              Optional for now. Split the total in one go or in parts, and finish it whenever you are ready.
            </span>
          </div>

          {parts.map((part, index) => (
            <div key={part.key} className="split-part-card">
              <div className="split-part-header">
                <span className="split-part-title">{part.name.trim() || `Part ${index + 1}`}</span>
                <button
                  type="button"
                  className="btn-tiny"
                  aria-label={`Remove part ${index + 1}`}
                  onClick={() => setParts(parts.filter((p) => p.key !== part.key))}
                >
                  Remove
                </button>
              </div>

              <div className="form-row-2">
                <div className="form-group">
                  <label htmlFor={`part-name-${part.key}`}>Name for part {index + 1} (optional)</label>
                  <input
                    id={`part-name-${part.key}`}
                    type="text"
                    maxLength={80}
                    placeholder="e.g. Rooms, Bar tab"
                    value={part.name}
                    onChange={(e) => updatePart(part.key, { name: e.target.value })}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor={`part-amount-${part.key}`}>Amount for part {index + 1} (₹)</label>
                  <input
                    id={`part-amount-${part.key}`}
                    type="number"
                    step="0.01"
                    placeholder="0.00"
                    value={part.amount}
                    onChange={(e) => updatePart(part.key, { amount: e.target.value })}
                  />
                </div>
              </div>

              <SplitEditor
                users={members}
                currentUser={currentUser}
                amount={part.amount}
                splitType={part.splitType}
                onSplitTypeChange={(splitType) => updatePart(part.key, { splitType })}
                selectedParticipants={part.selectedParticipants}
                onSelectedParticipantsChange={(selectedParticipants) => updatePart(part.key, { selectedParticipants })}
                exactAmounts={part.exactAmounts}
                onExactAmountsChange={(exactAmounts) => updatePart(part.key, { exactAmounts })}
              />
            </div>
          ))}

          {total > 0 && (
            <div
              className={`remaining-amount-banner ${isOverAllocated ? 'mismatch' : unsplit > 0 ? 'pending' : 'zero'}`}
            >
              {isOverAllocated ? (
                <span>
                  ⚠️ The split is <strong>{formatMoney(Math.abs(unsplit))}</strong> more than the total
                </span>
              ) : unsplit > 0 ? (
                <span>
                  <strong>{formatMoney(unsplit)}</strong> not yet split. You can save and finish later; payments
                  are recorded but stay out of balances until the split is complete.
                </span>
              ) : (
                <span>✓ The whole total is split</span>
              )}
            </div>
          )}

          {!isOverAllocated && (unsplit > 0 || total <= 0) && (
            <button type="button" className="btn btn-secondary" onClick={addPart}>
              {parts.length === 0 ? '➕ Add a split' : '➕ Add another part'}
              {unsplit > 0 ? ` for the remaining ${formatMoney(unsplit)}` : ''}
            </button>
          )}

          <div className="form-group">
            <label htmlFor="item-expected-payer">Who will pay the rest? (optional)</label>
            <select id="item-expected-payer" value={expectedPayerId} onChange={(e) => setExpectedPayerId(e.target.value)}>
              <option value="">Not decided</option>
              {members.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.id === currentUser.id ? 'You' : u.name}
                </option>
              ))}
            </select>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitDisabled}>
              {loading ? 'Saving...' : isEditing ? 'Save Changes' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EventItemModal;
